/**
 * SSO Login Page
 * Hanterar Single Sign-On via SAML 2.0 eller OpenID Connect
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { buildSsoInitiateUrl } from '@/utils/ssoRedirect';

interface FederationInfo {
  organizationId: string;
  availableMethods: string[];
  saml: {
    isActive: boolean;
    entityId: string;
    metadataUrl: string;
  } | null;
  oidc: {
    isActive: boolean;
    providerName: string;
  } | null;
}

const SsoLogin: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [federationInfo, setFederationInfo] = useState<FederationInfo | null>(null);

  // Hämta organizationId från URL eller query params
  const organizationId = searchParams.get('org') || searchParams.get('organizationId');

  useEffect(() => {
    if (!organizationId) {
      setError(t('auth.noOrganizationId') || 'Ingen organisation angiven');
      setLoading(false);
      return;
    }

    // Hämta federation info för organisationen
    fetchFederationInfo(organizationId);
  }, [organizationId]);

  const fetchFederationInfo = async (orgId: string) => {
    try {
      const response = await fetch(`/api/auth/federation/info/${orgId}`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Could not fetch federation info');
      }

      setFederationInfo(data.data);

      const ssoMethods = data.data.availableMethods.filter((m: string) => m !== 'PASSWORD');
      if (ssoMethods.length === 1) {
        if (ssoMethods[0] === 'SAML2') {
          initiateSamlLogin(orgId);
        } else if (ssoMethods[0] === 'OIDC') {
          initiateOidcLogin(orgId);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Kunde inte hämta inloggningsinformation');
    } finally {
      setLoading(false);
    }
  };

  const initiateSamlLogin = (orgId: string) => {
    const redirectUrl = searchParams.get('redirect') || '/';
    window.location.replace(
      buildSsoInitiateUrl({
        organizationId: orgId,
        authMethod: 'SAML2',
        redirect: redirectUrl,
      })
    );
  };

  const initiateOidcLogin = (orgId: string) => {
    const redirectUrl = searchParams.get('redirect') || '/';
    const loginHint = searchParams.get('login_hint') || searchParams.get('email');
    window.location.replace(
      buildSsoInitiateUrl({
        organizationId: orgId,
        authMethod: 'OIDC',
        email: loginHint || undefined,
        redirect: redirectUrl,
      })
    );
  };

  const getProviderDisplayName = (providerName: string): string => {
    const names: Record<string, string> = {
      'entra_id': 'Microsoft Entra ID',
      'inera_siths': 'SITHS',
      'google': 'Google',
      'okta': 'Okta',
      'custom': 'SSO Provider',
    };
    return names[providerName] || providerName;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-slate-300">{t('common.loading') || 'Laddar...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="bg-slate-800 border border-red-500/30 rounded-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('auth.loginError') || 'Inloggningsfel'}
            </h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              {t('auth.backToLogin') || 'Tillbaka till inloggning'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!federationInfo) {
    return null;
  }

  const hasSaml = federationInfo.saml?.isActive;
  const hasOidc = federationInfo.oidc?.isActive;
  const hasPassword = federationInfo.availableMethods.includes('PASSWORD');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            {t('auth.ssoLogin') || 'Single Sign-On'}
          </h1>
          <p className="text-slate-400">
            {t('auth.chooseLoginMethod') || 'Välj inloggningsmetod'}
          </p>
        </div>

        <div className="space-y-4">
          {hasOidc && federationInfo.oidc && (
            <button
              onClick={() => initiateOidcLogin(federationInfo.organizationId)}
              className={`w-full flex items-center justify-center gap-3 px-6 py-3 text-white rounded-lg transition-colors font-medium ${
                federationInfo.oidc.providerName === 'inera_siths'
                  ? 'bg-teal-600 hover:bg-teal-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {federationInfo.oidc.providerName === 'entra_id' && (
                <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                  <path d="M0 0h10.931v10.931H0zM12.069 0H23v10.931H12.069zM0 12.069h10.931V23H0zM12.069 12.069H23V23H12.069z"/>
                </svg>
              )}
              {federationInfo.oidc.providerName === 'inera_siths' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
                </svg>
              )}
              {t('auth.loginWith') || 'Logga in med'} {getProviderDisplayName(federationInfo.oidc.providerName)}
            </button>
          )}

          {hasSaml && (
            <button
              onClick={() => initiateSamlLogin(federationInfo.organizationId)}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {t('auth.loginWithSaml') || 'Logga in med SAML'}
            </button>
          )}

          {hasPassword && (hasSaml || hasOidc) && (
            <>
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-slate-600"></div>
                <span className="text-slate-500 text-sm">{t('common.or') || 'eller'}</span>
                <div className="flex-1 h-px bg-slate-600"></div>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('auth.loginWithPassword') || 'Logga in med lösenord'}
              </button>
            </>
          )}
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-slate-400 hover:text-white text-sm transition-colors"
          >
            ← {t('auth.backToLogin') || 'Tillbaka till inloggning'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SsoLogin;
